// ==================== ENUMS ====================
export enum PersonaEnum {
	SALARIED = 'SALARIED',
	SELF_EMPLOYED = 'SELF_EMPLOYED',
	// FREELANCER = 'FREELANCER'
}

export enum SectorEnum {
	GovernmentCivilian = 'GOVERNMENT_CIVILIAN',
	Retired = 'RETIRED',
	SemiGovernment = 'SEMI_GOVERNMENT',
	GovernmentMilitary = 'GOVERNMENT_MILITARY',
	ListedCompany = 'LISTED_COMPANY',
	Bankers = 'BANKERS',
	Private = 'PRIVATE',
	Freelancer = 'FREELANCER',
	SelfEmployed = 'SELF_EMPLOYED'
}

export enum ProductEnum {
	STANDARD = 'STANDARD',
	SELF_CONSTRUCT = 'SELF_CONSTRUCT',
	OFF_PLAN = 'OFF_PLAN'
}

export enum BuildingType {
	VILLA = 'VILLA',
	APARTMENT = 'APARTMENT',
}

export enum SubsidyLoanType {
	NEW_FINANCING = 'NEW_FINANCING',
	DOWN_PAYMENT_SUPPORT_PACKAGE = 'DOWN_PAYMENT_SUPPORT_PACKAGE'
}

export enum MaritalStatusEnum {
	Single = 'SINGLE',
	Married = 'MARRIED',
	Divorced = 'DIVORCED',
	Widowed = 'WIDOWED'
}

export enum ResidentialTypeEnum {
	HomeOwner = 'HOME_OWNER',
	WithFamily = 'WITH_FAMILY',
	Rented = 'RENTED'
}

export enum GenderEnum {
	Male = 'MALE',
	Female = 'FEMALE'
}

export enum AdditionalIncomeTypeEnum {
	PRIVATE_BUSINESS = 'PRIVATE_BUSINESS',
	INCENTIVES = 'INCENTIVES',
	RENTAL = 'RENTAL',
	SECOND_JOB = 'SECOND_JOB',
	PENSION = 'PENSION'
}

export enum OwnershipTypeEnum {
	SOLE = 'SOLE',
	JOINT = 'JOINT'
}

export enum EligibilityStatusEnum {
	Eligible = 'ELIGIBLE',
	NotEligible = 'NOT_ELIGIBLE'
}

export enum NotEligibleReasonType {
	PERMANENT = 'PERMANENT',
	TEMPORARY = 'TEMPORARY'
}

// ==================== INTERFACES ====================

export interface DateOfBirth {
	gregorian: string; // YYYY-MM-DD format
	hijri?: string; // YYYY-MM-DD format
}

export interface PersonalInformation {
	dateOfBirth: DateOfBirth;
	gender: GenderEnum;
	firstName: string;
	familyName: string;
	mobileNumber: string; // 9-15 digits without country code
}

export interface Expenses {
	rent: number;
	foodAndBeverages: number;
	education: number;
	healthCare: number;
	utilities: number;
	transportation: number;
	insurance: number;
	futureExpenses: number;
	others: number;
}

export interface Property {
	propertyId: string;
	productType: ProductEnum;
	price: number;
	buildingType?: BuildingType;
}

export interface RequestedFinancialParameters {
	downPayment: number;
	tenor: number;
	includeSubsidy: boolean;
	isFirstHome: boolean;
	subsidyLoanType?: SubsidyLoanType;
	ownershipPercentage?: number; // 0-100. SOLE must = 100, JOINT must be < 100
}

export interface ApplicantData {
	nationalId: string;
	nationality: string;
	profileId: string;
	sector: SectorEnum;
	persona: PersonaEnum;
	personalInformation: PersonalInformation;
	maritalStatus: MaritalStatusEnum;
	residentialType: ResidentialTypeEnum;
	numberOfDependents: number;
	numberOfDependentsInSchool: number;
	numberOfCarsOwned: number;
	educationalLevel?: EducationalLevelEnum;
	expenses: Expenses;
	additionalIncome?: {
		type: AdditionalIncomeTypeEnum;
	};
}

export interface JointApplicationData {
	mainApplicant: ApplicantData;
	coApplicants: ApplicantData[];
	property?: Property;
	requestedFinancialParameters?: RequestedFinancialParameters;
}

// ==================== CALCULATOR ENDPOINT (POST /application/calculator) ====================
// Single-applicant request body for POST /api/v1/application/calculator.

export interface CalculatorApplicant {
	nationalId: string;
	nationality: string;
	profileId: string;
	firstName: string;
	lastName: string;
	gender: GenderEnum;
	mobileNumber: string;
	maritalStatus: MaritalStatusEnum;
	residentialType: ResidentialTypeEnum;
	numberOfDependents: number;
	numberOfDependentsInSchool: number;
	numberOfCarsOwned: number;
	sector: SectorEnum;
	persona: PersonaEnum;
	educationalLevel?: EducationalLevelEnum;
	personalInformation: {
		dateOfBirth: DateOfBirth;
	};
	expenses: Expenses;
	additionalIncome?: {
		type: AdditionalIncomeTypeEnum;
	};
}

export interface CalculatorRequest {
	ownershipType: OwnershipTypeEnum;
	status?: 'DRAFT' | 'FINAL';
	forceCreditReportRefresh?: boolean;
	applicant: CalculatorApplicant;
	property: Property;
	requestedFinancialParameters: RequestedFinancialParameters;
}

// ==================== ELIGIBILITY RESULT TYPES ====================

export interface ReasonObject {
	code: string;
	message: string;
}

export interface EligibleResult {
	status: EligibilityStatusEnum.Eligible;
}

export interface PermanentNotEligibleResult {
	status: EligibilityStatusEnum.NotEligible;
	type: NotEligibleReasonType.PERMANENT;
	reason: ReasonObject;
}

export interface TemporaryNotEligibleResult {
	status: EligibilityStatusEnum.NotEligible;
	type: NotEligibleReasonType.TEMPORARY;
	reason: ReasonObject;
	daysBeforeReapply: number;
}

export type NotEligibleResult = PermanentNotEligibleResult | TemporaryNotEligibleResult;

export type EligibilityResult = EligibleResult | NotEligibleResult;

// ==================== FINANCIAL PARAMETERS ====================

export interface FinancialParameters {
	maximumBorrowingPower: number;
	maximumAffordablePropertyPrice: number;
	maximumEqualMonthlyInstallment: number;
	maximumTenure: number;
}

// ==================== API RESPONSE ====================

export interface JointApplicationSuccessEligibleResponse {
	applicationId: string;
	eligibility: EligibleResult;
	financialParameters: FinancialParameters;
}

export interface JointApplicationSuccessNotEligibleResponse {
	applicationId: string;
	eligibility: NotEligibleResult;
}

export type JointApplicationSuccessResponse =
	| JointApplicationSuccessEligibleResponse
	| JointApplicationSuccessNotEligibleResponse;

export interface ApiResponse<T> {
	ok: boolean;
	data: T;
	message?: string;
}

// ==================== ACTUAL API RESPONSE TYPES ====================
// These match the real API response format

export interface JointApplicationApplicant {
	id: string;
	profileId: string;
	nationalId: string;
	nationality: string;
	personalInformation: {
		birthDate: string;
		gender: string;
		maritalStatus: string;
		firstName: string;
		familyName: string;
		lastName: string;
		mobileNumber: string;
		age: number;
	};
	employmentData: any;
	creditReport: any;
	sector: string;
	persona: string;
	residentialType: string;
	numberOfDependents: number;
	numberOfDependentsInSchool: number;
	numberOfCarsOwned: number;
	expenses: Expenses;
	createdAt: string;
	updatedAt: string;
}

// ==================== NEW STAGE INTERFACES ====================

export interface ScorecardAttribute {
	attributeName: string;
	matched: boolean;
	value?: any;
	factorPoints: number;
	weight: number;
}

export interface ScorecardResult {
	success: boolean;
	message: string;
	score: number;
	riskGrade: string;
	attributes: ScorecardAttribute[];
}

export interface ScorecardStageResult {
	scorecards: Record<string, ScorecardResult>;
}

export interface PersonaMatchingResult {
	persona: {
		roi: number;
		maxFinanceAmount: number;
		approvingBody: string;
	};
	input: {
		nationality: string;
		sector: string;
		score: number;
	};
}

export interface PostPersonaMatchingResult {
	financeAmount: number;
	financeAmountIncludingProfit: number;
	emi: number;
	ltv: number;
	tenor: number;
	totalNewFinancialObligation: number;
	currentDbr: number;
	newCombinedDbr: number;
	disposableIncome: number;
	obligationsToDisposableIncomeRatio: number;
}

export interface RepaymentCheckResult {
	passedDBRCheck: boolean;
	repaymentCalculations: {
		financeAmount: number;
		financeAmountIncludingProfit: number;
		newMinDownPaymentPercent: number;
		newMinDownPayment: number;
		newMaximumPropertyPrice: number;
		emi: number;
		tenor: number;
	};
}

export interface KnockoutCheck {
	isEligible: boolean;
	value: number;
	min?: number;
	max?: number;
}

export interface KnockoutIndividualResult {
	success: boolean;
	isEligible: boolean;
	eligibilityErrors: any[];
	checks: Record<string, KnockoutCheck>;
	policy: Record<string, number>;
}

export interface KnockoutStageResult {
	knockoutChecksResults: Record<string, KnockoutIndividualResult>;
	knockoutResult: KnockoutIndividualResult;
}

export interface DeviationCheck {
	status: string;
	value?: number;
	deviationValue?: number;
	allowedDeviationPercentage?: number;
	type?: string;
	unit?: string;
}

export interface DeviationResult {
	isRejected: boolean;
	deviationsCount: number;
	deviationsChecks?: Record<string, DeviationCheck>;
}

export interface DeviationStageResult {
	deviationResults: Record<string, DeviationResult>;
	financialDeviations: DeviationResult;
}

export interface InvestmentPortfolioBorrower {
	ageYears: number;
	gender: string;
	maritalStatus: string;
	city: string;
	educationLevel: string;
	monthlySalary: string;
}

export interface InvestmentPortfolioLoanRequest {
	sector: string;
	productType: string;
	loanTenureYears: number;
	creditScore: number;
}

export interface InvestmentPortfolioRequest {
	borrower: InvestmentPortfolioBorrower;
	loanRequest: InvestmentPortfolioLoanRequest;
}

export interface FinanceCalculationResult {
	financeAmount: number;
	matchPortfolioIds: string[];
	matchInvestmentIds: string[];
	rate: number;
	contractTypes: string[];
	emi: number;
	ltv: number;
	financeAmountIncludingProfit: number;
}

export interface InvestmentMatch {
	effectiveAnnualFlatRatePercent: string;
	minFinanceAmount: string;
	maxFinanceAmount: string;
	investableAmount: string;
	contractTypes: string[];
	portfolioIds: string[];
	investmentIds: string[];
}

export interface InvestmentPortfolioResult {
	totalMatches: number;
	eligibleMatches: number;
	request: InvestmentPortfolioRequest;
	financeCalculationResult: FinanceCalculationResult;
	matches: InvestmentMatch[];
}

export interface RejectionReason {
	code: string;
	message: string;
	daysBeforeReapply?: number;
	applicantId?: string;
}

export interface FinalDecisionResult {
	status: string;
	rejectionReasons: RejectionReason[];
	financeAmount: number;
	financeAmountPlusProfit: number;
	emi: number;
	tenor: number;
}

export interface StageData<T> {
	status: string;
	outputResult: {
		success: boolean;
		stageName: string;
		data: T;
		rejectionReasons?: RejectionReason[];
		metadata?: any;
	};
}

// ==================== LIST LOAN APPLICATIONS ====================

export type DecisionOutcome = 'APPROVED' | 'REJECTED' | 'APPROVED_WITH_DEVIATION' | 'PROCESSING';
export type ApplicationStatus = 'ALL' | 'INITIAL' | 'DRAFT' | 'FINAL';
export type SortByField = 'createdAt' | 'dealAmount' | 'status' | 'outcomeStatus';
export type SortOrder = 'asc' | 'desc';
export type GroupBy = 'application' | 'product';

export interface LoanApplicationListItem {
	applicationId: string;
	mainApplicant: {
		customerId: string;
		name: string;
		profileId: string;
	};
	decisionOutcome: DecisionOutcome;
	rejectionReasons: string[];
	applicationStatus: string;
	coApplicants: any[];
	dealAmount: number;
	propertyId: string;
	maturity: number;
	decisionDate: string;
}

export interface LoanApplicationListPagination {
	total: number;
	page: number;
	limit: number;
	totalPages: number;
}

export interface LoanApplicationListResponse {
	success: boolean;
	data: {
		items: LoanApplicationListItem[];
		pagination: LoanApplicationListPagination;
	};
}

export interface LoanApplicationListFilters {
	page: number;
	limit: number;
	sortBy: SortByField;
	sortOrder: SortOrder;
	groupBy?: GroupBy;
	startDate?: string;
	endDate?: string;
	outcomeStatus?: DecisionOutcome;
	applicationId?: string;
	applicationStatus?: ApplicationStatus;
}

export interface JointApplicationResponse {
	applicationId: string;
	application: {
		id: string;
		ownershipType?: string;
		status: string;
		primaryApplicant: JointApplicationApplicant;
		coApplicants: JointApplicationApplicant[];
		property?: Property;
		createdAt: string;
		updatedAt: string;
	};
	       stages: {
		       EMPLOYMENT_DATA?: StageData<any>;
		       OPEN_BANKING?: StageData<any>;
		       COMMERCIAL_OPEN_BANKING?: StageData<any>;
		       NON_SALARIED_RAC?: StageData<any>;
		       RAC_POLICY?: StageData<any>;
		       COMMERCIAL_RAC_POLICY?: StageData<any>;
		       CREDIT_REPORT?: StageData<any>;
		       CREDIT_BUREAU?: StageData<any>;
		       CONDITIONAL_APPROVAL?: StageData<any>;
		       INITIAL_ELIGIBILITY_CHECKS?: StageData<any>;
		       RESPONSIBLE_LENDING?: StageData<any>;
		       UNIFIED_INCOME?: StageData<any>;
		       MAXIMUM_BORROWING_POWER?: StageData<any>;
		       POST_PROPERTY_SELECTION?: StageData<any>;
		       SCORECARD_EVALUATION?: StageData<ScorecardStageResult>;
		       COMMERCIAL_SCORECARD_EVALUATION?: StageData<ScorecardStageResult>;
		       PERSONA_MATCHING?: StageData<PersonaMatchingResult>;
		       POST_PERSONA_MATCHING?: StageData<PostPersonaMatchingResult>;
		       REPAYMENT_CALCULATIONS?: StageData<RepaymentCheckResult>;
		       KNOCKOUT?: StageData<KnockoutStageResult>;
		       DEVIATION?: StageData<DeviationStageResult>;
		       POST_DEVIATION_STAGE?: StageData<any>;
		       POST_DEVIATION_CREDIT_BUREAU?: StageData<any>;
		       FINAL_ELIGIBILITY_CHECKS?: StageData<any>;
		       ADDITIONAL_INCOME?: StageData<any>;
		       INVESTMENT_PORTFOLIO?: StageData<InvestmentPortfolioResult>;
		       REDF_CALCULATION?: StageData<any>;
		       FINAL_DECISION?: StageData<FinalDecisionResult>;
	       };
}

// ==================== COMMERCIAL APPLICATION TYPES ====================

export enum CommercialProductTypeEnum {
	COMMERCIAL_STANDARD = 'COMMERCIAL_STANDARD',
}

export enum RegionEnum {
	ABHA = 'ABHA',
	ABQAIQ = 'ABQAIQ',
	ABU_ARISH = 'ABU_ARISH',
	AFIF = 'AFIF',
	ALKHARJ = 'ALKHARJ',
	ALQUWAYIYAH = 'ALQUWAYIYAH',
	ALULA = 'ALULA',
	AL_AFLAJ = 'AL_AFLAJ',
	AL_AHSA = 'AL_AHSA',
	AL_AQIQ = 'AL_AQIQ',
	AL_BADAI = 'AL_BADAI',
	AL_BAHA = 'AL_BAHA',
	AL_BUKAYRIYAH = 'AL_BUKAYRIYAH',
	AL_DARB = 'AL_DARB',
	AL_GHAZALAH = 'AL_GHAZALAH',
	AL_HINAKIYAH = 'AL_HINAKIYAH',
	AL_JUMUM = 'AL_JUMUM',
	AL_KHARJ = 'AL_KHARJ',
	AL_KHURMAH = 'AL_KHURMAH',
	AL_LITH = 'AL_LITH',
	AL_MAJMAAH = 'AL_MAJMAAH',
	AL_MAJMA_AH = 'AL_MAJMA_AH',
	AL_MAKHWAH = 'AL_MAKHWAH',
	AL_MANDAQ = 'AL_MANDAQ',
	AL_MIDHNAB = 'AL_MIDHNAB',
	AL_MUZAHIMIYAH = 'AL_MUZAHIMIYAH',
	AL_NAIRYAH = 'AL_NAIRYAH',
	AL_NAMAS = 'AL_NAMAS',
	AL_QUNFUDHAH = 'AL_QUNFUDHAH',
	AL_QUWAY_IYAH = 'AL_QUWAY_IYAH',
	AL_ULA = 'AL_ULA',
	AL_UWAYQILAH = 'AL_UWAYQILAH',
	AL_WAJH = 'AL_WAJH',
	AL_ZULFI = 'AL_ZULFI',
	ARAR = 'ARAR',
	AR_RASS = 'AR_RASS',
	ASH_SHINAN = 'ASH_SHINAN',
	BADR = 'BADR',
	BADR_AL_JANUB = 'BADR_AL_JANUB',
	BAISH = 'BAISH',
	BALJURASHI = 'BALJURASHI',
	BAQAA = 'BAQAA',
	BISHA = 'BISHA',
	BURAIDAH = 'BURAIDAH',
	DAMMAM = 'DAMMAM',
	DAWADMI = 'DAWADMI',
	DHAHRAN_AL_JANUB = 'DHAHRAN_AL_JANUB',
	DIRIYAH = 'DIRIYAH',
	DUBA = 'DUBA',
	DUMAT_AL_JANDAL = 'DUMAT_AL_JANDAL',
	FARASAN = 'FARASAN',
	HAFAR_AL_BATIN = 'HAFAR_AL_BATIN',
	HAIL = 'HAIL',
	HAQL = 'HAQL',
	HAWTAT_BANI_TAMIM = 'HAWTAT_BANI_TAMIM',
	HUBUNA = 'HUBUNA',
	JAZAN_ABHA = 'JAZAN_ABHA',
	JEDDAH = 'JEDDAH',
	JUBAIL = 'JUBAIL',
	KHAFJI = 'KHAFJI',
	KHAMIS_MUSHAIT = 'KHAMIS_MUSHAIT',
	KHAYBAR = 'KHAYBAR',
	KHOBAR = 'KHOBAR',
	KHULAYS = 'KHULAYS',
	MADINAH = 'MADINAH',
	MAHD_AL_THAHAB = 'MAHD_AL_THAHAB',
	MAKKAH = 'MAKKAH',
	MUHAYIL = 'MUHAYIL',
	NAJRAN = 'NAJRAN',
	QATIF = 'QATIF',
	QURAYYAT = 'QURAYYAT',
	RABIGH = 'RABIGH',
	RAFHA = 'RAFHA',
	RAS_TANURA = 'RAS_TANURA',
	RIJAL_ALMA = 'RIJAL_ALMA',
	RIYADH = 'RIYADH',
	RIYAD_AL_KHABRA = 'RIYAD_AL_KHABRA',
	SABYA = 'SABYA',
	SAKAKA = 'SAKAKA',
	SAMTAH = 'SAMTAH',
	SARAT_ABIDAH = 'SARAT_ABIDAH',
	SHAQRA = 'SHAQRA',
	SHARURAH = 'SHARURAH',
	TABARJAL = 'TABARJAL',
	TABUK = 'TABUK',
	TAIF = 'TAIF',
	TATHLITH = 'TATHLITH',
	TAYMA = 'TAYMA',
	TURABAH = 'TURABAH',
	TURAIF = 'TURAIF',
	UMLUJ = 'UMLUJ',
	UNAIZAH = 'UNAIZAH',
	WADI_AD_DAWASIR = 'WADI_AD_DAWASIR',
	WADI_ADDAWASIR = 'WADI_ADDAWASIR',
	YANBU = 'YANBU',
}

export enum ContractTypeEnum {
	MURABAHA = 'MURABAHA',
	IJARA = 'IJARA',
}

export enum EducationalLevelEnum {
	NONE = 'NONE',
	HIGH_SCHOOL = 'HIGH_SCHOOL',
	DIPLOMA = 'DIPLOMA',
	BACHELORS = 'BACHELORS',
	MASTERS = 'MASTERS',
	DOCTORATE = 'DOCTORATE',
	PHD = 'PHD',
}

export enum SourceOfIncomeEnum {
	SALARIED = 'SALARIED',
	SELF_EMPLOYED = 'SELF_EMPLOYED',
	REAL_ESTATE_YIELD = 'REAL_ESTATE_YIELD',
}

export enum RentFrequencyEnum {
	MONTHLY = 'MONTHLY',
	QUARTERLY = 'QUARTERLY',
	SEMI_ANNUALLY = 'SEMI_ANNUALLY',
	ANNUALLY = 'ANNUALLY',
}

export interface PropertyRentUnit {
	startDate: string; // ISO 8601 date, e.g. 2025-01-01
	endDate: string; // ISO 8601 date, e.g. 2025-12-31. Units with endDate before today are ignored.
	income: number; // Rent for a single period of the given frequency, in SAR (>= 0)
	frequency: RentFrequencyEnum;
	totalAnnualIncome: number; // Total annual rent for the unit, in SAR (>= 0)
}

export interface CommercialProperty {
	propertyId: string;
	productType: CommercialProductTypeEnum;
	price: number;
	region: RegionEnum;
	propertyRentUnits: PropertyRentUnit[];
}

export interface CommercialRequestedFinancialParameters {
	downPayment: number;
	tenor: number;
	ownershipPercentage: number; // 0-100. SOLE must = 100, JOINT must be < 100
}

export interface CommercialPrimaryApplicant {
	nationalId: string;
	nationality: string;
	profileId: string;
	gender: GenderEnum;
	firstName: string;
	lastName: string;
	mobileNumber: string;
	personalInformation: {
		dateOfBirth: DateOfBirth;
	};
	maritalStatus: MaritalStatusEnum;
	residentialType: ResidentialTypeEnum;
	numberOfDependents: number;
	numberOfDependentsInSchool: number;
	numberOfCarsOwned: number;
	expenses: Expenses;
	sector: SectorEnum;
	persona: PersonaEnum;
	educationalLevel: EducationalLevelEnum;
	sourceOfIncome: SourceOfIncomeEnum[];
	rentalAssignment: boolean;
	rePortfolioSize: number;
}

export interface CommercialApplicationData {
	ownershipType: string;
	primaryApplicant: CommercialPrimaryApplicant;
	property: CommercialProperty;
	requestedFinancialParameters: CommercialRequestedFinancialParameters;
}

export interface CommercialApplicationResponse {
	applicationId: string;
	ownershipType: string;
	eligibility: {
		status: string;
		ineligibilityType?: 'PERMANENT' | 'TEMPORARY';
		ineligibilityReason?: {
			code: string;
			message: string;
		};
	};
	financialParameters?: {
		financeAmount: number;
		equatedMonthlyInstallment: number;
		tenure: number;
		minimumDownPayment: number;
		minimumDownPaymentPercent: number;
		annualProfitRate: number;
		murabahaRate: number;
		propertyPrice: number;
		contractType: string;
	};
}


